import { useEffect, useState } from 'react';
import axios from 'axios';
import { X, ExternalLink, Loader2, AlertTriangle, Building2 } from 'lucide-react';

/**
 * FilerHoldingsModal — Feb 2026.
 *
 * Drill-down for the 13-F "Top Institutional Holders" row click.
 * Shows the manager's most-recent-quarter top positions ranked by
 * market value — answers "what else does this fund own besides
 * the stock I'm looking at?"
 *
 * Server: GET /api/filer/{cik}/holdings?limit=25
 *   • returns up to 25 top positions for the latest filed quarter
 *   • cached 7d server-side; modal cold-load typically 1–2s
 *
 * Admin-only paywall proxy (same as the Insider tab itself) until
 * billing ships; the 403 path renders a friendly notice rather than
 * an error.
 */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtMoney = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(1)}K`;
  return `$${n.toFixed(0)}`;
};
const fmtShares = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('en-US', { maximumFractionDigits: 0 });
};
const fmtPeriod = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  } catch {
    return iso;
  }
};

export default function FilerHoldingsModal({ open, onClose, filerCik, filerName }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !filerCik) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    axios.get(`${API}/filer/${encodeURIComponent(filerCik)}/holdings`, {
      params: { limit: 25 },
      withCredentials: true,
    })
      .then((res) => {
        if (cancelled) return;
        setData(res.data);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err?.response?.status === 403) {
          setError('Admin-only preview. Full filer drill-down coming with the paid tier.');
        } else {
          setError(err?.response?.data?.detail || 'Could not load filer holdings.');
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, filerCik]);

  // ESC dismiss
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const totalMv = Number(data?.total_market_value) || 0;
  const headerName = data?.filer_name || filerName || (filerCik ? `CIK ${filerCik}` : 'Filer');

  return (
    <div
      className="fixed inset-0 z-[90] bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4"
      onClick={onClose}
      data-testid="filer-holdings-modal"
    >
      <div
        className="bg-card border rounded-xl shadow-2xl w-full max-w-2xl max-h-[88vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-4 pt-4 pb-3 border-b border-border">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Building2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
              <h3 className="text-sm font-bold truncate" data-testid="filer-holdings-name">{headerName}</h3>
            </div>
            <p className="text-[11px] text-muted-foreground leading-snug">
              Top 25 positions by market value
              {data?.as_of_period ? <> · As of {fmtPeriod(data.as_of_period)}</> : null}
              {totalMv > 0 ? <> · Total AUM {fmtMoney(totalMv)}{data?.total_positions ? ` across ${data.total_positions.toLocaleString()} positions` : ''}</> : null}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-md hover:bg-muted text-muted-foreground"
            data-testid="filer-holdings-close"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-3 sm:p-4 pb-24 sm:pb-4">
          {loading && (
            <div className="flex items-center justify-center gap-2 py-12 text-muted-foreground text-sm">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading holdings…
            </div>
          )}
          {!loading && error && (
            <div className="flex items-start gap-2 p-3 rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-300 dark:border-amber-700 text-[12px]">
              <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-300 shrink-0 mt-0.5" />
              <span className="text-amber-900 dark:text-amber-200">{error}</span>
            </div>
          )}
          {!loading && !error && data && (
            data.rows.length === 0 ? (
              <p className="text-[12px] text-muted-foreground py-6 text-center">
                No recent 13-F positions found for this filer.
              </p>
            ) : (
              <div className="overflow-x-auto rounded-md border border-border">
                <table className="min-w-full text-[11px]" data-testid="filer-holdings-table">
                  <thead className="bg-muted/40">
                    <tr className="text-[10px] uppercase tracking-wide text-muted-foreground">
                      <th className="px-2 py-1.5 text-left">#</th>
                      <th className="px-2 py-1.5 text-left">Issuer</th>
                      <th className="px-2 py-1.5 text-right">Shares</th>
                      <th className="px-2 py-1.5 text-right">Market Value</th>
                      <th className="px-2 py-1.5 text-right">% of AUM</th>
                      <th className="px-2 py-1.5 text-center">Doc</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.rows.map((r, idx) => {
                      const mv = Number(r.market_value) || 0;
                      const pct = totalMv > 0 ? (mv / totalMv) * 100 : 0;
                      return (
                        <tr key={`${r.cusip || ''}-${idx}`} className="border-t border-border/40 hover:bg-muted/20">
                          <td className="px-2 py-1.5 text-muted-foreground">{idx + 1}</td>
                          <td className="px-2 py-1.5">
                            <div className="font-semibold text-foreground truncate max-w-[280px]" title={r.issuer_name}>
                              {r.issuer_name || '—'}
                            </div>
                            <div className="text-[10px] text-muted-foreground">
                              {r.title_of_class || ''}
                              {r.put_call ? <> · <span className="uppercase">{r.put_call}</span></> : null}
                            </div>
                          </td>
                          <td className="px-2 py-1.5 text-right font-mono">{fmtShares(r.shares)}</td>
                          <td className="px-2 py-1.5 text-right font-mono font-bold">{fmtMoney(r.market_value)}</td>
                          <td className="px-2 py-1.5 text-right font-mono text-muted-foreground">
                            {pct >= 0.01 ? `${pct.toFixed(2)}%` : '<0.01%'}
                          </td>
                          <td className="px-2 py-1.5 text-center">
                            {r.filing_url ? (
                              <a
                                href={r.filing_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center text-muted-foreground hover:text-foreground"
                                title="Open SEC filing in new tab"
                              >
                                <ExternalLink className="w-3 h-3" />
                              </a>
                            ) : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )
          )}
          {!loading && !error && data?.filer_cik && (
            <p className="text-[10px] text-muted-foreground mt-3 text-center">
              Source: SEC Form 13-F · CIK {data.filer_cik}
              {data.cached ? ' · cached' : ' · live'}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
